import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings, LogOut, LogIn, UserPlus, User } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import { ThemeOptions } from "@/components/ThemeOptions";

const itemCls = "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-t-ink hover:bg-t-bg transition-colors";

export const AccountMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const guest = user.is_guest;
  const close = () => setOpen(false);
  const signOut = async () => {
    close();
    navigate("/", { replace: true });
    await logout();
    toast("Signed out");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="account-menu-button" aria-label="Account and settings"
          className="flex items-center gap-3 px-2 py-2 rounded-xl text-left hover:bg-t-surface-hover/60 transition-colors w-full min-w-0">
          <span className="h-9 w-9 shrink-0 rounded-full bg-t-primary text-white flex items-center justify-center font-semibold text-sm">
            {guest ? <User className="h-4 w-4" /> : (user.name || "?")[0].toUpperCase()}
          </span>
          <span className="hidden sm:block min-w-0">
            <span className="block text-sm font-medium truncate text-t-ink" data-testid="account-menu-trigger-name">{guest ? "Guest" : user.name}</span>
            <span className={`block text-[11px] truncate ${guest ? "text-t-primary font-medium" : "text-t-muted"}`}>{guest ? "Sign up to save" : "Account & settings"}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" collisionPadding={12} className="z-[60] w-80 max-w-[calc(100vw-24px)] shadow-xl bg-t-card border-t-line rounded-2xl p-2" data-testid="account-menu">
        <div className="px-3 py-3 border-b border-t-line mb-2">
          <p className="font-serif-display text-xl font-semibold leading-tight" data-testid="account-menu-name">{guest ? "Guest" : user.name}</p>
          <p className="text-xs text-t-muted truncate">{guest ? "Trying the app — sign up to save" : user.email}</p>
        </div>
        {guest ? (
          <>
            <Link to="/login?mode=signup" onClick={close} data-testid="account-menu-signup"
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 mb-1 rounded-xl bg-t-primary hover:bg-t-primary-hover text-white text-sm font-medium transition-colors">
              <UserPlus className="h-4 w-4" /> Sign up to save
            </Link>
            <Link to="/login" onClick={close} data-testid="account-menu-signin" className={itemCls}><LogIn className="h-4 w-4" /> Sign in</Link>
          </>
        ) : (
          <Link to="/settings" onClick={close} data-testid="account-menu-settings" className={itemCls}><Settings className="h-4 w-4" /> Account settings</Link>
        )}
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-t-muted px-3 pt-3 pb-1.5">Theme</p>
        <ThemeOptions />
        {!guest && (
          <button onClick={signOut} data-testid="account-menu-signout" className={`${itemCls.replace("text-t-ink", "text-t-danger")} mt-2 border-t border-t-line rounded-t-none`}>
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};
